import { Button, ButtonGroup, Css, RightPaneLayout, ScrollableContent, Switch, useModal } from "@homebound/beam";
import { useState } from "react";
import { useNavigate } from "react-router";
import { SearchBox } from "src/components";
import { PageBidPackageDetailFragment } from "src/routes/bids/BidPackageDetailPage.generated";
import { PlanBasedBidsTableV2 } from "src/routes/bids/plan-based-bid-package/components/PlanBasedBidsTableV2";
import { TableActions } from "src/routes/layout/TableActions";
import { UploadBidV2Modal } from "../plan-based-bid-package/components/UploadBidV2Modal";
import { PlanBasedBidsTable } from "./PlanBasedBidsTable";
import { UnitBasedBidsTable } from "./UnitBasedBidsTable";
import { getBidPackageTradesWithEstimate, goToEnterCostsPage } from "./utils";

type BidsTabProps = { bidPackage: PageBidPackageDetailFragment };

export function BidsTab(props: BidsTabProps) {
  const { bidPackage } = props;
  const { openModal } = useModal();
  const navigate = useNavigate();
  const [searchFilter, setSearchFilter] = useState<string | undefined>("");
  const [showPlanUsage, setShowPlanUsage] = useState(!bidPackage.isUnitBased);
  const hasUnacceptedChanges = bidPackage.availableUpdates.nonEmpty;
  const [scopeView, setScopeView] = useState<"hide" | "all" | "changes">(hasUnacceptedChanges ? "changes" : "hide");
  const [showUncostedItemsOnly, setShowUncostedItemsOnly] = useState(false);

  const trades = getBidPackageTradesWithEstimate(bidPackage);
  const requestsWithEditableBids = trades.filter((trade) => {
    const bidContractRevision = trade.bidContractRevision;
    return !bidContractRevision || bidContractRevision.canEdit.allowed || bidContractRevision.canReviseCosts.allowed;
  });

  return (
    <>
      <TableActions>
        <div css={Css.df.aic.gap2.$}>
          <SearchBox onSearch={setSearchFilter} />
          {bidPackage.isUnitBased && (
            <>
              <div css={Css.df.aic.gap1.$}>View: </div>
              <ButtonGroup
                size="md"
                buttons={[
                  { text: "All Items", onClick: () => setShowUncostedItemsOnly(false), active: !showUncostedItemsOnly },
                  {
                    text: "Uncosted only",
                    onClick: () => setShowUncostedItemsOnly(true),
                    active: showUncostedItemsOnly,
                  },
                ]}
              />
            </>
          )}
          {bidPackage.isPlanBasedV2 && (
            <div css={Css.df.aic.gap1.$}>
              <span>View: </span>
              <ButtonGroup
                size="md"
                buttons={[
                  { text: "Hide Scope", onClick: () => setScopeView("hide"), active: scopeView === "hide" },
                  { text: "All Scope", onClick: () => setScopeView("all"), active: scopeView === "all" },
                  ...(hasUnacceptedChanges
                    ? [
                        {
                          text: "Changes Only",
                          onClick: () => setScopeView("changes"),
                          active: scopeView === "changes",
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          )}
        </div>
        {bidPackage.isUnitBased && (
          <Switch label="Show Plan Usage" selected={showPlanUsage} onChange={setShowPlanUsage} />
        )}
        {bidPackage.isPlanBasedV2 && (
          <div css={Css.df.gap1.$}>
            <Button
              label="Enter Costs"
              onClick={() => goToEnterCostsPage(bidPackage, bidPackage.activeBidders, openModal, navigate)}
              variant="textSecondary"
              icon="pencil"
            />
            <Button
              label="Upload Costs"
              onClick={() => openModal({ content: <UploadBidV2Modal bidPackage={bidPackage} /> })}
              variant="textSecondary"
              icon="upload"
            />
          </div>
        )}
      </TableActions>
      <div css={Css.h100.$}>
        {!showPlanUsage ? (
          // Since the plan based table has tabs the scrollable content is wrapped inside that component
          <ScrollableContent virtualized>
            <RightPaneLayout>
              <div css={Css.h100.$}>
                <UnitBasedBidsTable
                  bidPackage={bidPackage}
                  searchFilter={searchFilter}
                  requestsWithEditableBids={requestsWithEditableBids}
                  showUncostedItemsOnly={showUncostedItemsOnly}
                />
              </div>
            </RightPaneLayout>
          </ScrollableContent>
        ) : bidPackage.isPlanBasedV2 ? (
          <PlanBasedBidsTableV2 bidPackage={bidPackage} searchFilter={searchFilter} scopeView={scopeView} />
        ) : (
          <PlanBasedBidsTable
            bidPackage={bidPackage}
            searchFilter={searchFilter}
            requestsWithEditableBids={requestsWithEditableBids}
            showUncostedItemsOnly={showUncostedItemsOnly}
          />
        )}
      </div>
    </>
  );
}

export function getOptionsKey(opt: { __typename?: "ReadyPlanOption" | undefined; id: string }[]) {
  return opt
    .sortByKey("id")
    .map((o) => o.id)
    .join(",");
}
